// SEQ-180 phase 2, task P13: one request key's rows turned into the buckets a
// generic widget draws. Pure module, no React, so the chart components file
// only exports components (react-refresh/only-export-components) and this can
// be proven under Deno.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { isSafeModeActive } from "../../lib/calculations";
import { formatMonthLabel } from "./pnlAnalyticsFormat";
import { metricsToRollup, sumRollups, type PnlRollup } from "./rollup";
import { foldMetric, ROLLUP_BINDING_BY_METRIC } from "./widgetPresentation";
import { comparisonMatchKey } from "./periodBucket";

export interface WidgetBucket {
  key: string;
  label: string;
  projectId: string | null;
  periodMonth: string | null;
  value: number | null;
  secondary: number | null;
  comparison: number | null;
}

/**
 * Axis tick text for a unit, short enough not to crowd the axis.
 *
 * SAFE MODE (React review, CRITICAL). Every value formatter this feature uses
 * already masks at source: `formatCurrency` and `formatCompactCurrency` in
 * lib/calculations.ts return their mask when Safe Mode is on, so every KPI
 * card, tooltip, table cell, delta chip and drill-down row was already
 * covered. This function was the exception, and the only one: it built
 * currency ticks by hand, so a user-built bar or line on a dollar metric
 * printed real magnitudes ("1235k") straight onto the axis with the page's
 * own "dollar values hidden" badge showing.
 *
 * The rule is the same one `formatMetricValue` follows and the plan records
 * as F31: currency and currency_per_hour are masked, and NOTHING else is.
 * Percent, hours, count and ratio are not money and stay readable, which is
 * what makes Safe Mode usable for a screen share about overtime rather than
 * a blank page. An empty tick is used rather than the dot mask because a
 * masked axis label would be re-measured and re-laid-out by Recharts at a
 * width that has nothing to do with the real one; this matches
 * `compactCurrencyTick` in lib/calculations.ts, which already returns "" for
 * exactly this reason.
 */
export function axisTickFormatter(unit: PnlUnit): (value: number) => string {
  if (unit === "percent") return (v) => `${v}%`;
  if (unit === "hours") return (v) => `${v}h`;
  if (unit === "currency" || unit === "currency_per_hour") {
    return (v) => {
      if (isSafeModeActive()) return "";
      return Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : String(v);
    };
  }
  return (v) => String(v);
}

/** True when a unit carries money, and is therefore masked under Safe Mode. */
export function unitIsMasked(unit: PnlUnit): boolean {
  return unit === "currency" || unit === "currency_per_hour";
}

/** The label the RPC gives a company-grain row (D17), reused for a client-side company fold. */
export const COMPANY_BUCKET_LABEL = "All projects";

/**
 * One metric's company-wide value from project-grain rows. A metric with a
 * rollup binding (the eight phase 1 KPI metrics) is read off `sumRollups`,
 * sum-then-divide, so a ratio is the ratio of the sums and never a mean of
 * per-project ratios (AC10). Any other metric goes through `foldMetric`,
 * which sums a sum metric and refuses (null) to re-fold an avg, min, max or
 * latest one, because those must be fetched at company grain instead.
 */
function companyValueFor(rows: PnlAnalyticsRow[], totals: PnlRollup, metricKey: string, config: PnlWidgetConfig): number | null {
  const binding = ROLLUP_BINDING_BY_METRIC[metricKey];
  if (binding) {
    const raw = totals[binding.field];
    return typeof raw === "number" ? raw : null;
  }
  return foldMetric(rows, metricKey, config.aggregation).value;
}

/**
 * Browser E2E HIGH (D17 on the display side): a widget whose `groupBy` is
 * `company` fetched at project grain (the planner keeps a rollup-bound or
 * sum metric on that fetch so the KPI cards stay pixel-identical) must still
 * DRAW one bucket, not one per project. This folds the project rows into one
 * "All projects" bucket per organisation, using the same `sumRollups` the
 * KPI cards read, so a table or bar on net profit at company grouping shows
 * exactly the KPI card's total. Rows already at company grain (no projectId)
 * pass through untouched: the RPC folded them over cells.
 */
function foldToCompanyBuckets(
  config: PnlWidgetConfig,
  rows: PnlAnalyticsRow[],
  comparisonRows: PnlAnalyticsRow[] | null,
): WidgetBucket[] {
  const byOrg = new Map<string, PnlAnalyticsRow[]>();
  for (const row of rows) {
    const group = byOrg.get(row.organizationId) ?? [];
    group.push(row);
    byOrg.set(row.organizationId, group);
  }
  const comparisonByOrg = new Map<string, PnlAnalyticsRow[]>();
  for (const row of comparisonRows ?? []) {
    const group = comparisonByOrg.get(row.organizationId) ?? [];
    group.push(row);
    comparisonByOrg.set(row.organizationId, group);
  }

  const buckets: WidgetBucket[] = [];
  for (const [organizationId, orgRows] of byOrg) {
    const totals = sumRollups(orgRows.map((r) => metricsToRollup(r.metrics)));
    const priorRows = comparisonByOrg.get(organizationId) ?? [];
    const priorTotals = sumRollups(priorRows.map((r) => metricsToRollup(r.metrics)));
    buckets.push({
      key: organizationId,
      label: COMPANY_BUCKET_LABEL,
      projectId: null,
      periodMonth: null,
      value: companyValueFor(orgRows, totals, config.metric, config),
      secondary: config.secondaryMetric ? companyValueFor(orgRows, totals, config.secondaryMetric, config) : null,
      comparison: priorRows.length > 0 ? companyValueFor(priorRows, priorTotals, config.metric, config) : null,
    });
  }
  return buckets;
}

/**
 * Applies the config's own sort and limit. A month-grain bucket is labelled by
 * its month and always sorted chronologically: a trend read out of order is
 * worse than one that ignores the sort field.
 */
export function toWidgetBuckets(
  config: PnlWidgetConfig,
  rows: PnlAnalyticsRow[],
  comparisonRows: PnlAnalyticsRow[] | null,
): WidgetBucket[] {
  const projectGrainRows = rows.length > 0 && rows.every((r) => r.projectId !== null && r.periodMonth === null);
  if (config.groupBy === "company" && projectGrainRows) {
    return foldToCompanyBuckets(config, rows, comparisonRows);
  }

  // SEQ-240 (audit B, row 22). A month, quarter or year bucket's key contains
  // its period, so the prior period's key never equals the current one and a
  // match on the whole key left the comparison blank. Those grains are matched
  // by period offset instead; every other grain still matches on the key.
  const kind = config.comparison?.kind;
  const matchKeyOf = (row: PnlAnalyticsRow, side: "current" | "prior"): string =>
    comparisonMatchKey(config.groupBy, row.groupKey, kind, side) ?? row.groupKey;
  const comparisonByKey = new Map((comparisonRows ?? []).map((r) => [matchKeyOf(r, "prior"), r]));

  const buckets: WidgetBucket[] = rows.map((row) => ({
    key: row.groupKey,
    label: row.periodMonth ? formatMonthLabel(row.periodMonth) : row.groupLabel,
    projectId: row.projectId,
    periodMonth: row.periodMonth,
    value: row.metrics[config.metric] ?? null,
    secondary: config.secondaryMetric ? (row.metrics[config.secondaryMetric] ?? null) : null,
    comparison: comparisonByKey.get(matchKeyOf(row, "current"))?.metrics[config.metric] ?? null,
  }));

  const isPeriodGrain = buckets.length > 0 && buckets.every((b) => b.periodMonth !== null);
  if (isPeriodGrain) {
    buckets.sort((a, b) => (a.periodMonth ?? "").localeCompare(b.periodMonth ?? ""));
  } else if (config.sort) {
    const direction = config.sort.direction === "asc" ? 1 : -1;
    buckets.sort((a, b) => {
      const av = a.value ?? Number.NEGATIVE_INFINITY;
      const bv = b.value ?? Number.NEGATIVE_INFINITY;
      return (av - bv) * direction;
    });
  }

  if (typeof config.limit === "number" && config.limit > 0) return buckets.slice(0, config.limit);
  return buckets;
}

/**
 * The phase 1 widgets whose own component carries controls the widget config
 * has no field for (the scatter axis toggle, the trend project selector, the
 * table's column visibility and its exports). The renderer hands these their
 * existing component rather than drawing them generically, which is what
 * keeps the default dashboard identical to the phase 1 page.
 */
export const PHASE1_WIDGET_IDS: ReadonlySet<string> = new Set([
  "bar-profit-percent-by-project",
  "scatter-profit-vs-overtime",
  "scatter-profit-vs-patching",
  "table-project-performance",
  "line-monthly-trend",
]);
